'use client';
import { alpha, Box, Button, Typography, useTheme } from '@mui/material';

import { useT } from '@/hooks';
import { TradeTab } from '@/types';

interface TradeSummaryProps {
  activeTab: TradeTab;
  onTrade: () => void;
  outcomeName: string;
  amount: string;
  price: number;
  isSubmitting?: boolean;
  isAuthenticated?: boolean;
  isReadyToTrade?: boolean;
  isInsufficientBalance?: boolean;
  isInputInvalid?: boolean;
}

export function TradeSummary({
  activeTab,
  onTrade,
  outcomeName,
  amount,
  price,
  isSubmitting,
  isAuthenticated,
  isReadyToTrade,
  isInsufficientBalance,
  isInputInvalid,
}: TradeSummaryProps) {
  const { t } = useT();
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isBuy = activeTab === 'buy';

  const amountNum = parseFloat(amount) || 0;

  // Ensure price is treated as a number
  const numericPrice = Number(price);
  const avgPrice = numericPrice * 100;

  // Use numericPrice for calculations to avoid type issues
  const estShares = numericPrice > 0 ? (amountNum / numericPrice).toFixed(1) : 'Infinity';
  const potentialReturn = numericPrice > 0 ? (amountNum / numericPrice).toFixed(2) : 'Infinity';
  const profitPercent = numericPrice > 0 ? Math.round((1 / numericPrice - 1) * 100) : 'Infinity';

  const rowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    mb: { xs: 1, sm: 1.5 },
  };

  const labelStyle = {
    color: 'text.secondary',
    fontSize: '0.9375rem',
    fontWeight: 600,
  };

  const valueStyle = {
    color: 'text.primary',
    fontSize: '1rem',
    fontWeight: 800,
  };

  const summaryBg = isDark ? alpha(theme.palette.text.primary, 0.05) : theme.palette.action.hover;

  return (
    <Box sx={{ px: { xs: '12px', sm: '16px' }, pb: { xs: '20px', sm: '20px' } }}>
      <Box
        sx={{
          bgcolor: summaryBg,
          borderRadius: { xs: '16px', sm: '24px' },
          p: { xs: '12px', sm: '16px' },
          mb: { xs: 1.5, sm: 1.5 },
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Box sx={rowStyle}>
          <Typography sx={labelStyle}>{t('trade.avgPrice')}</Typography>
          <Typography sx={valueStyle}>
            {typeof avgPrice === 'number' ? avgPrice.toFixed(1) : avgPrice}¢
          </Typography>
        </Box>
        <Box sx={rowStyle}>
          <Typography sx={labelStyle}>{t('trade.estShares')}</Typography>
          <Typography sx={valueStyle}>{estShares}</Typography>
        </Box>
        <Box
          sx={{
            ...rowStyle,
            mb: 0,
            pt: 1.5,
            borderTop: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography sx={labelStyle}>{t('trade.potentialReturn')}</Typography>
          <Typography sx={{ ...valueStyle, color: '#4ec38a' }}>
            ${potentialReturn} (+{profitPercent}%)
          </Typography>
        </Box>
      </Box>

      <Button
        fullWidth
        variant="contained"
        onClick={onTrade}
        disabled={isSubmitting || (isInputInvalid && isReadyToTrade)}
        sx={{
          py: { xs: 1.5, sm: 1 },
          borderRadius: { xs: '16px', sm: '24px' },
          fontSize: { xs: '1rem', sm: '1.15rem' },
          fontWeight: 900,
          textTransform: 'none',
          boxShadow: `0 12px 32px ${alpha(theme.palette.primary.main, 0.4)}`,
          bgcolor: 'primary.main',
          color: 'primary.contrastText',
          '&:hover': {
            bgcolor: 'primary.dark',
            boxShadow: `0 16px 40px ${alpha(theme.palette.primary.main, 0.5)}`,
            transform: 'translateY(-2px)',
          },
          '&.Mui-disabled': {
            bgcolor: 'action.disabledBackground',
            color: 'action.disabled',
          },
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        }}
      >
        {(() => {
          if (isSubmitting) return t('trade.processing');
          if (!isAuthenticated) return t('trade.trade');
          if (isInsufficientBalance) return t('trade.deposit');
          if (!isReadyToTrade) return t('trade.loading');
          return isBuy
            ? t('trade.buyAction', { outcome: outcomeName, amount })
            : t('trade.sellAction', { outcome: outcomeName, amount });
        })()}
      </Button>
    </Box>
  );
}
